import React, { useState } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
  Divider,
  Stack,
  DialogContent,
} from '@mui/material';
import { add, displayMoney, minus, moneyToMetric } from '../../../../utils/money';
import { PieChart } from '@mui/x-charts';
import { CashFlowData } from '../cash-flow/utils';
import Grid2 from '@mui/material/Unstable_Grid2';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { IconWithText } from '../../../common/styles/components';
import { Summarize } from '@mui/icons-material';
import { isMobile, isTablet } from 'react-device-detect';

interface CashFlowSummaryProps {
  data: CashFlowData;
}

const generateData = (c: CashFlowData) => {
  const deliveryProfit = c.drivers.profit;
  const expenses = c.expenses.total;
  const pendingMerchantPayment = c.merchants.pending_total;

  const netProfit = minus(deliveryProfit, expenses);

  const netCashIn = add(netProfit, pendingMerchantPayment)

  return {
    netProfit,
    expenses,
    pendingMerchantPayment,
    netCashIn,
  };
};

export default function CashFlowSummary({ data }: CashFlowSummaryProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { netProfit, expenses, pendingMerchantPayment, netCashIn } = generateData(data);

  const pieData = [
    {
      id: 'netProfit',
      value: moneyToMetric(netProfit).toNumber(),
      label: 'Net Profit',
      formattedValue: displayMoney(netProfit),
    },
    {
      id: 'pendingMerchantPayment',
      value: moneyToMetric(pendingMerchantPayment).toNumber(),
      label: 'Pending Merchant Payment',
      formattedValue: displayMoney(pendingMerchantPayment),
    },
  ];

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setIsOpen(true)}>
        <IconWithText>
          <Summarize color="primary" fontSize="small" />
          Show Summary
        </IconWithText>
      </Button>

      <CustomDialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="lg" fullWidth fullScreen={(isMobile && !isTablet)}>
        <DialogContent>
        <Box sx={{ mt: 2, }}>
          <Grid2 container spacing={4}>
            <Grid2 xs={12} md={3}>
              <Stack spacing={3}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" align="center">
                    Net Profit
                  </Typography>
                  <Typography variant="h4" align="center" color="primary">
                    {displayMoney(netProfit)}
                  </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" align="center">
                    Total Expenses
                  </Typography>
                  <Typography variant="h4" align="center" color="error">
                    {displayMoney(expenses)}
                  </Typography>
                </Paper>

                <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="body2" align="center" color="text.secondary">
                    Net Cash In
                  </Typography>
                  <Typography variant="h6" align="center" fontWeight="bold">
                    {displayMoney(netCashIn)}
                  </Typography>
                </Paper>
              </Stack>
            </Grid2>

            <Grid2 xs={12} md={9}>
           <div
                style={{padding: 5,  width: '100%', display:"flex", flexDirection: "column", justifyContent: "center", alignItems: "center", justifyItems: "center", alignContent: "center", height: "auto", }}
                >
        <Typography variant="subtitle1" align="center" sx={{ mb: 2, textAlign: "center" }}>
            Net Cash In Breakdown
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
           <PieChart
  series={[
    {
      data: pieData,
      valueFormatter: (_, context) => pieData[context.dataIndex].formattedValue,
      innerRadius: 60,
      outerRadius: 100,
      paddingAngle: 4,
      cornerRadius: 4,
      highlightScope: { faded: 'global', highlighted: 'item' },
      faded: {
        innerRadius: 30,
        additionalRadius: -30,
        color: 'gray',
      },
    },
  ]}
  margin={{ top: 20, bottom: 120 }} // more bottom space for legend
  slotProps={{
    legend: {
      direction: (isMobile && !isTablet) ? 'column' : 'row',
      position: {
        horizontal: 'middle',
        vertical: 'bottom',
      },
    },
  }}
  width={(isMobile && !isTablet) ? 300 : 400}
  height={(isMobile && !isTablet) ? 350 : 300}
/>
        </div></div>



            </Grid2>
          </Grid2>
        </Box>
        </DialogContent>
      </CustomDialog>
    </>
  );
}
